import { useEffect, useState } from 'react';
import z from 'zod';
import { ConfirmResetPassword, useConfirmResetPasswordMutation, useResetPasswordMutation } from '../../api/auth';
import { Button } from '../../components/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/Dialog';
import { PasswordInput, TextInput } from '../../components/Input/TextInput';
import { passwordRegExp } from '../../constants';
import { useToaster } from '../../hooks/useToaster';
import { useValidator } from '../../hooks/useValidator';

type ResetPasswordModalProps = {
  userId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type ResetPasswordFormProps = {
  onSubmit: () => void;
  isUpdating: boolean;
};

type ChangePasswordFormProps = {
  onSubmit: () => void;
  isUpdating: boolean;
  onChange: (property: string, value: string | string[]) => void;
  errors: Record<string, string>;
};

const ResetPasswordForm = ({ onSubmit, isUpdating }: ResetPasswordFormProps) => (
  <>
    <DialogHeader>
      <DialogTitle>Reset Password</DialogTitle>
      <DialogDescription>By clicking confirm, we will send an email to reset your password.</DialogDescription>
    </DialogHeader>
    <DialogFooter>
      <Button variant="accent" onClick={onSubmit} disabled={isUpdating} isLoading={isUpdating}>
        Confirm
      </Button>
      <DialogClose asChild>
        <Button variant="transparent">Cancel</Button>
      </DialogClose>
    </DialogFooter>
  </>
);

const ChangePasswordForm = (props: ChangePasswordFormProps) => (
  <>
    <DialogHeader>
      <DialogTitle>Change Password</DialogTitle>
      <DialogDescription>
        We have sent a password reset code to your email. Enter it below to reset your password.
      </DialogDescription>
    </DialogHeader>
    <div className="flex flex-col justify-between">
      <div className="pb-5">
        <div className="flex gap-5">
          <div className="flex max-w-[400px]">
            <TextInput
              label="Confirmation code"
              placeholder="Enter confirmation code"
              onChange={(value) => props.onChange('confirmationCode', value)}
              errorMessage={props.errors.confirmationCode}
            />
          </div>
        </div>
        <div className="flex gap-5">
          <div className="mt-5 flex max-w-[400px]">
            <PasswordInput
              label="New password"
              placeholder="Enter new password"
              onChange={(value) => props.onChange('password', value)}
              errorMessage={props.errors.password}
            />
          </div>
        </div>
        <div className="flex gap-5">
          <div className="mt-5 flex max-w-[400px]">
            <PasswordInput
              label="Confirm new password"
              placeholder="Re-enter new password"
              onChange={(value) => props.onChange('passwordConfirmation', value)}
              errorMessage={props.errors.passwordConfirmation}
            />
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="accent" onClick={props.onSubmit} disabled={props.isUpdating} isLoading={props.isUpdating}>
          Change Password
        </Button>
        <DialogClose asChild>
          <Button variant="transparent">Cancel</Button>
        </DialogClose>
      </DialogFooter>
    </div>
  </>
);

export const ResetPasswordModal = ({ userId, open, onOpenChange }: ResetPasswordModalProps) => {
  const [showChangePasswordForm, setShowChangePasswordForm] = useState(false);
  const toast = useToaster();

  const [resetPassword, { isLoading: isUpdatingReset }] = useResetPasswordMutation();

  const [changePassword, { isLoading: isUpdatingChange, error: errorChange, isSuccess: isSuccessChange }] =
    useConfirmResetPasswordMutation();

  const [changePasswordRequest, setChangePasswordRequest] = useState<
    ConfirmResetPassword & { passwordConfirmation: string }
  >({
    userId: userId,
    confirmationCode: '',
    password: '',
    passwordConfirmation: ''
  });

  const { validate, errors } = useValidator(
    z
      .object({
        confirmationCode: z.string().min(1, 'A confirmation code is required'),
        password: z
          .string()
          .regex(
            passwordRegExp,
            'Please enter a valid password with a minimum length of 8 characters, with one lowercase letter, one uppercase letter, one number, and one special character'
          )
          .min(1, 'A password is required'),
        passwordConfirmation: z.string().min(1, 'A password confirmation is required')
      })
      .refine(({ password, passwordConfirmation }) => passwordConfirmation === password, {
        message: 'Passwords need to match',
        path: ['passwordConfirmation']
      })
  );

  const onChange = (property: string, value: string | string[]) => {
    setChangePasswordRequest({ ...changePasswordRequest, [property]: value });
  };

  useEffect(() => {
    if (isSuccessChange) {
      toast.success('Password reset successfully.');
      onOpenChange(false);
    } else if (errorChange) {
      if ('data' in errorChange && typeof errorChange.data === 'string') {
        toast.error(errorChange.data);
      } else {
        toast.error('Failed to reset password. Please try again.');
      }
    }
  }, [isSuccessChange, errorChange, onOpenChange, toast]);

  const submitResetPassword = async () => {
    try {
      await resetPassword(userId).unwrap();
      setShowChangePasswordForm(true);
    } catch {
      toast.error('Failed to send the reset password email. Please try again.');
    }
  };

  const submitChangePassword = async () => {
    if (!validate(changePasswordRequest)) {
      return;
    }

    await changePassword(changePasswordRequest);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {showChangePasswordForm ? (
          <ChangePasswordForm
            onSubmit={submitChangePassword}
            isUpdating={isUpdatingChange}
            onChange={onChange}
            errors={errors}
          />
        ) : (
          <ResetPasswordForm onSubmit={submitResetPassword} isUpdating={isUpdatingReset} />
        )}
      </DialogContent>
    </Dialog>
  );
};
